import clsx from "clsx";
import { BackButton, SegmentedButton, TabGroup } from "components";
import { ErrorHelper } from "helpers";
import { useIsMobile } from "hooks/useIsMobile";
import { Application } from "models/Application";
import ApplicationPageDocuments from "pages/Backoffice/ApplicationPage/tabs/Documents";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApplicationService } from "services/applicationService";
import ApplicationAnalyses from "./ApplicationAnalyses";
import ApplicationDetail from "./ApplicationDetail";
import "./styles.scss";
import {
	ApplicationDetailParams,
	ApplicationPageTabs,
	TabContent
} from "./types";

const getSegmentsList = (
	tabs: Record<ApplicationPageTabs, TabContent>
): TabContent[] => {
	return Object.values(tabs);
};

export default function ApplicationPage() {
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const [application, setApplication] = useState<Application>();
	const [loading, setLoading] = useState(true);
	const applicationService = useMemo(ApplicationService.getInstance, []);
	const { applicationId, selectedTab } = useParams<
		keyof ApplicationDetailParams
	>() as ApplicationDetailParams;

	const fetchApplication = async () => {
		setLoading(true);
		try {
			const response = await applicationService.retrieveMyApplication(
				applicationId
			);
			setApplication(response);
		} catch (error) {
			ErrorHelper.showErrorMessage(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchApplication();
	}, [applicationId]);

	const setSelectedTab = useCallback(
		(newSelectedTab: ApplicationPageTabs) => {
			navigate(`/solicitacao/${applicationId}/${newSelectedTab}`);
		},
		[navigate]
	);

	useEffect(() => {
		const isValidTab = Object.values(ApplicationPageTabs).includes(selectedTab);
		if (!isValidTab) {
			navigate(`/solicitacao/${applicationId}/${ApplicationPageTabs.DETAILS}`);
		}
	}, [selectedTab, applicationId, navigate]);

	const applicationTabs: Record<ApplicationPageTabs, TabContent> = useMemo(
		() => ({
			[ApplicationPageTabs.DETAILS]: {
				id: ApplicationPageTabs.DETAILS,
				onClick: () => setSelectedTab(ApplicationPageTabs.DETAILS),
				label: "Solicitação",
				component: ApplicationDetail
			},
			[ApplicationPageTabs.ANALYSIS]: {
				id: ApplicationPageTabs.ANALYSIS,
				onClick: () => setSelectedTab(ApplicationPageTabs.ANALYSIS),
				label: "Análise",
				component: ApplicationAnalyses
			},
			[ApplicationPageTabs.DOCUMENTS]: {
				id: ApplicationPageTabs.DOCUMENTS,
				onClick: () => setSelectedTab(ApplicationPageTabs.DOCUMENTS),
				label: "Documentos",
				component: ApplicationPageDocuments
			}
		}),
		[application]
	);

	const currentTabContent: TabContent = useMemo(
		() => applicationTabs[selectedTab],
		[selectedTab]
	);
	const segments = useMemo(
		() => getSegmentsList(applicationTabs),
		[applicationTabs]
	);

	return (
		<div className="application-page">
			<div className="application-header">
				<div className="application-back-title">
					{!isMobile && <BackButton prevRoute="/" />}
					<div className="application-title">
						Processo
						{!!application && ` - ${application?.applicationProcessCode}`}
					</div>
				</div>
				<div
					className={clsx("flex flex-row my-1", {
						"w-full": isMobile
					})}
				>
					{isMobile ? (
						<TabGroup tabs={segments} activeTab={selectedTab} />
					) : (
						<SegmentedButton segments={segments} selected={selectedTab} />
					)}
				</div>
			</div>
			{currentTabContent && (
				<currentTabContent.component
					loading={loading}
					application={application}
					isBackoffice={false}
					isMobile={isMobile}
				/>
			)}
		</div>
	);
}
